import * as React from 'react';
import {
  Building2Icon,
  LayoutDashboard,
  Users,
  FileText,
  Cog,
  Play,
  Receipt,
  Search,
  ClipboardList,
  ClipboardCheck,
  ToggleLeft,
  Ticket,
  SquareKanban,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@common-web/components/ui/sidebar';
import {NavMain, type NavItem} from '@common-web/components/nav-main';
import {NavUser} from '@common-web/components/nav-user';
import {useAuthStore} from '@common-web/stores/authStore';
import {useUserAvatarUrl} from '@common-web/hooks/useUserAvatarUrl';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {trpc} from '@common-web/lib/trpc';
import {STALE_TIME_1_MIN} from '@common/constants/time';
import {
  resolveInternalMenuItemEnabled,
  type InternalFeaturesShape,
  type InternalMenuKey,
} from '@common/menuFlags';

// Keyed nav-item shape used while building the candidate menu. The `menuKey`
// and `alwaysVisible` markers are stripped before passing items to `NavMain`.
// Visibility is default-deny: a leaf renders only when its `menuKey` resolves
// visible, or when it is explicitly marked `alwaysVisible` (reserved for items
// gated purely by permissions, e.g. the Internal Flags console — intentionally
// unkeyed so it can never hide itself).
type KeyedNavItem = Omit<NavItem, 'items'> & {
  menuKey?: InternalMenuKey;
  alwaysVisible?: boolean;
  items?: KeyedNavItem[];
};

/**
 * Top-level entries any internal user may see (menu flags still apply). Every
 * other top-level entry needs a permission case in `AppSidebar`'s role gate, or
 * it is hidden (default-deny).
 *
 * Matched by `title`, so renaming an entry in `internalNavData` without updating
 * this list hides it.
 */
const UNGATED_INTERNAL_NAV_TITLES: ReadonlySet<string> = new Set([
  'Groups Legacy',
  'Jobs',
  'Invoices',
  'VBA Sync Runs',
  'VBA Sync Runs - CAS',
]);

/**
 * TEMPORARY — menu items hidden while the previous internal portal runs
 * alongside this one on its own URL for user comparison. Delete this list and
 * its use in `filterMenu` once that portal is retired.
 *
 * TODO: Ticket to remove: https://app.asana.com/1/1206214889537900/project/1206881612175705/task/1218607242450901?focus=true
 *
 * Not feature flags: those live in a single global `InternalConfig` row with no
 * per-portal scoping, so toggling one changes the nav for every portal pointed
 * at that API — including the old one, whose menus are the thing being
 * compared. The deployed bundle is the only thing that differs between the two,
 * so the list has to live in code.
 *
 * One-way: this hides items the flags would show, and never reveals one they
 * hide. Deals has no key on its parent, so all five children are listed to
 * collapse the section.
 */
const DEPLOYMENT_HIDDEN_MENU_KEYS: ReadonlySet<InternalMenuKey> = new Set([
  'internal.accounts',
  'internal.deals.create',
  'internal.deals.admin',
  'internal.deals.rfcSubmissions',
  'internal.deals.onboardingPipeline',
  'internal.deals.onboardingTemplates',
]);

// Filter a tree of keyed candidates by `resolveInternalMenuItemEnabled`,
// default-deny. `features === null` means the config query has not succeeded
// (still loading, or failed — e.g. a non-internal caller hitting the
// internalProcedure): every flag-gated item is hidden rather than falling back
// to permissive defaults. Parent sections are hidden when no child survives.
function filterMenu(items: KeyedNavItem[], features: InternalFeaturesShape | null): NavItem[] {
  const isKeyEnabled = (key: InternalMenuKey) =>
    features !== null && resolveInternalMenuItemEnabled(key, features);

  const filtered: NavItem[] = [];
  for (const item of items) {
    if (item.menuKey && DEPLOYMENT_HIDDEN_MENU_KEYS.has(item.menuKey)) continue;
    if (item.menuKey && !isKeyEnabled(item.menuKey)) continue;

    const childCandidates = item.items;
    if (childCandidates && childCandidates.length > 0) {
      const filteredChildren = filterMenu(childCandidates, features);
      if (filteredChildren.length === 0) continue;
      const {menuKey: _ignored, alwaysVisible: _ignored2, ...rest} = item;
      filtered.push({...rest, items: filteredChildren});
      continue;
    }

    // Default-deny for leaves: require an explicit grant.
    if (!item.menuKey && !item.alwaysVisible) continue;

    const {menuKey: _ignored, alwaysVisible: _ignored2, ...rest} = item;
    filtered.push(rest);
  }
  return filtered;
}

// Navigation for internal admin portal
const internalNavData: {navMain: KeyedNavItem[]} = {
  navMain: [
    {
      // Permission-gated below (groupPipeline.view); children have no menuKey.
      title: 'Groups',
      icon: SquareKanban,
      items: [
        {title: 'Group Pipeline', url: '/group-pipeline', alwaysVisible: true},
        {title: 'Pipeline Builder', url: '/group-pipeline/builder', alwaysVisible: true},
      ],
    },
    {
      title: 'Groups Legacy',
      url: '/groups',
      icon: Building2Icon,
      menuKey: 'internal.groups',
    },
    {
      title: 'Member Search',
      url: '/member-search',
      icon: Search,
      menuKey: 'internal.memberSearch',
    },
    {
      // Permission-gated below (enrollment.view); no menuKey, so it isn't menu-flag controlled.
      title: 'Enrollment',
      url: '/enrollment-sites',
      icon: ClipboardCheck,
      alwaysVisible: true,
    },
    {
      title: 'Accounts',
      url: '/accounts',
      icon: LayoutDashboard,
      menuKey: 'internal.accounts',
    },
    {
      title: 'Jobs',
      url: '/jobs',
      icon: Play,
      menuKey: 'internal.jobs',
    },
    {
      title: 'Invoices',
      url: '/invoices',
      icon: Receipt,
      menuKey: 'internal.invoices',
    },
    {
      title: 'Deals',
      icon: FileText,
      items: [
        {
          title: 'Create New Deal',
          url: '/rfc/create',
          menuKey: 'internal.deals.create',
        },
        {
          title: 'Deals Administration',
          url: '/rfc/admin',
          menuKey: 'internal.deals.admin',
        },
        {
          title: 'RFC Submissions',
          url: '/rfc/submissions',
          menuKey: 'internal.deals.rfcSubmissions',
        },
        {
          title: 'Onboarding Pipeline',
          url: '/onboarding',
          menuKey: 'internal.deals.onboardingPipeline',
        },
        {
          title: 'Onboarding Templates',
          url: '/onboarding/templates',
          menuKey: 'internal.deals.onboardingTemplates',
        } /*
        {
          title: 'Ongoing Maintenance',
          url: '/onboarding/maintenance',
        },*/,
      ],
    },
    {
      title: 'VBA Sync Runs',
      url: '/vba-sync',
      icon: Cog,
      menuKey: 'internal.vbaSync',
    },
    {
      title: 'VBA Sync Runs - CAS',
      url: '/vba-sync-cas',
      icon: Cog,
      menuKey: 'internal.vbaSync',
    },
    {
      title: 'Claims Tracking',
      url: '/claims',
      icon: ClipboardList,
      menuKey: 'internal.claims',
    },
    {
      title: 'Development Requests',
      url: '/tickets',
      icon: Ticket,
      menuKey: 'internal.tickets',
    },
    {
      title: 'Feature Flags',
      icon: ToggleLeft,
      items: [
        {
          // Permission-gated by `clients.manage_features` in `roleGated`.
          title: 'Client Flags',
          url: '/clients',
          menuKey: 'internal.clients',
        },
        {
          // Permission-gated by `internal_config.manage` in `roleGated`.
          // Intentionally unkeyed so the console that controls these flags can
          // never hide itself.
          title: 'Internal Flags',
          url: '/internal-features',
          alwaysVisible: true,
        },
      ],
    },
    {
      title: 'Users',
      url: '/users',
      icon: Users,
      items: [
        {
          title: 'All Users',
          url: '/users',
          menuKey: 'internal.users.all',
        },
        {
          title: 'Create User',
          url: '/users/new',
          menuKey: 'internal.users.create',
        },
        {
          // Nested under Users for navigation, but NOT under Users' permission
          // gate — see the `Users` branch in `roleGated`. Intentionally unkeyed
          // so the console that grants permissions can never be flag-hidden.
          title: 'Roles and permissions',
          url: '/permissions',
          alwaysVisible: true,
        },
      ],
    },
  ],
};

export function AppSidebar({
  logo,
  internalSpecificMenus,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  logo?: string;
  // Same default-deny filter as the built-in nav: items need a `menuKey` or
  // `alwaysVisible` to render.
  internalSpecificMenus?: KeyedNavItem[];
}) {
  const user = useAuthStore((state) => state.user);
  const {avatarUrl: avatarDisplayUrl} = useUserAvatarUrl();
  const canAccessClaimsTracking = useHasPermission(CODE_PERMISSIONS.claimsTracking.view.key);
  const canManagePermissions = useHasPermission(CODE_PERMISSIONS.permissions.manage.key);
  const canManageRoles = useHasPermission(CODE_PERMISSIONS.roles.manage.key);
  const canManageClientFlags = useHasPermission(CODE_PERMISSIONS.clients.manageFeatures.key);
  const canManageInternalFlags = useHasPermission(CODE_PERMISSIONS.internalConfig.manage.key);
  const canManageInternalTickets = useHasPermission(CODE_PERMISSIONS.tickets.manageInternal.key);
  // Member search results come from `vba.members.search`, which requires
  // `eligibility.view` — without it the page can only render errors.
  const canViewEligibility = useHasPermission(CODE_PERMISSIONS.eligibility.view.key);
  const canViewEnrollment = useHasPermission(CODE_PERMISSIONS.enrollment.view.key);
  const canViewGroupPipeline = useHasPermission(CODE_PERMISSIONS.groupPipeline.view.key);
  // Accounts, Users, and RFC Submissions are backed by
  // `*OrInternalAdmin` endpoints, which admit internal users only when they
  // hold `tenant.access_all` — without it those pages can only render errors.
  const hasTenantAccessAll = useHasPermission(CODE_PERMISSIONS.tenant.accessAll.key);

  // Global, org-wide internal feature flags. Queried directly (rather than read
  // from the session) so toggling in the console takes effect on the next page
  // load without invalidating sessions.
  const {data: configData, isError: configError} = trpc.internal.config.get.useQuery(undefined, {
    staleTime: STALE_TIME_1_MIN,
  });
  // Fail closed: until the config query succeeds, `filterMenu` hides every
  // flag-gated item (null = config unavailable). A failed fetch — e.g. a
  // non-internal user who can log in but is rejected by the internalProcedure —
  // must hide optional items, not reveal them via permissive defaults.
  const internalFeatures: InternalFeaturesShape | null =
    !configError && configData?.features ? (configData.features as InternalFeaturesShape) : null;

  // Permission gate first, then feature-flag filter.
  const roleGated = internalNavData.navMain.flatMap((item): KeyedNavItem[] => {
    if (item.title === 'Claims Tracking') return canAccessClaimsTracking ? [item] : [];
    if (item.title === 'Development Requests') return canManageInternalTickets ? [item] : [];
    if (item.title === 'Member Search') return canViewEligibility ? [item] : [];
    if (item.title === 'Enrollment') return canViewEnrollment ? [item] : [];
    if (item.title === 'Groups') return canViewGroupPipeline ? [item] : [];
    if (item.title === 'Accounts') return hasTenantAccessAll ? [item] : [];
    if (item.title === 'Users') {
      // Two independent gates under one parent. The user-management children
      // need `tenant.access_all` (their endpoints are `*OrInternalAdmin`);
      // "Roles and permissions" keeps its own `roles.manage` / `permissions
      // .manage` gate, so a role admin without `tenant.access_all` still
      // reaches it — via a Users parent holding only that child. The parent
      // disappears entirely when neither gate passes.
      const visibleChildren = (item.items ?? []).filter((child) =>
        child.url === '/permissions' ? canManagePermissions || canManageRoles : hasTenantAccessAll,
      );
      return visibleChildren.length > 0 ? [{...item, items: visibleChildren}] : [];
    }
    if (item.title === 'Deals') {
      // Only RFC Submissions is backed by a tenant.access_all-gated endpoint;
      // the other deal pages work for any internal user.
      const visibleChildren = (item.items ?? []).filter(
        (child) => child.url !== '/rfc/submissions' || hasTenantAccessAll,
      );
      return visibleChildren.length > 0 ? [{...item, items: visibleChildren}] : [];
    }
    if (item.title === 'Feature Flags') {
      // Each console is only visible to holders of its manage permission;
      // the section disappears entirely when both are hidden.
      const visibleChildren = (item.items ?? []).filter((child) =>
        child.url === '/internal-features' ? canManageInternalFlags : canManageClientFlags,
      );
      return visibleChildren.length > 0 ? [{...item, items: visibleChildren}] : [];
    }
    // Default-deny: a top-level entry with no case above renders only once it
    // is explicitly listed as needing no extra permission.
    return UNGATED_INTERNAL_NAV_TITLES.has(item.title) ? [item] : [];
  });
  const navItems = filterMenu([...roleGated, ...(internalSpecificMenus ?? [])], internalFeatures);

  const userData = {
    name: user?.name || 'User',
    email: user?.email || '',
    avatar: avatarDisplayUrl,
  };

  // Portal info for internal admin
  const portalInfo = {
    name: 'CBS Portal',
    subtitle: 'Internal Management System',
  };

  return (
    <Sidebar collapsible='offcanvas' {...props}>
      {/* Logo section above the header */}
      {logo && (
        <div className='px-4 py-2 border-b border-sidebar-border'>
          <div className='bg-white rounded-lg p-2'>
            <img src={logo} alt='Logo' className='w-full h-auto max-h-12 object-contain' />
          </div>
        </div>
      )}

      <SidebarHeader className='border-b border-sidebar-border'>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className='data-[state=open]:bg-sidebar-accent h-14 hover:bg-transparent'
            >
              <a href='#' className='flex items-center gap-3 px-3'>
                <div className='flex flex-col gap-0.5'>
                  <span className='text-base font-heading font-bold tracking-tight'>
                    {portalInfo.name}
                  </span>
                  <span className='text-xs text-muted-foreground'>{portalInfo.subtitle}</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <div className='px-4 py-2'>
          <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
            Admin Management
          </p>
        </div>
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  );
}
